import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
  type CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { config, isAuthConfigured } from '@/config';

/**
 * Thin promise wrapper over amazon-cognito-identity-js.
 *
 * Passwords go straight from the browser to Cognito over TLS using SRP (the password
 * itself is never sent) and never touch WeDrive's own API. The SDK stores tokens in
 * localStorage under its own keys and refreshes the access token automatically when
 * {@link getSession} is called, using the refresh token.
 */
let pool: CognitoUserPool | undefined;

function getPool(): CognitoUserPool {
  if (!isAuthConfigured()) {
    throw new Error('Sign-in is not configured. Set VITE_COGNITO_USER_POOL_ID and VITE_COGNITO_CLIENT_ID.');
  }
  pool ??= new CognitoUserPool({ UserPoolId: config.userPoolId, ClientId: config.clientId });
  return pool;
}

const userFor = (email: string) => new CognitoUser({ Username: email.trim().toLowerCase(), Pool: getPool() });

export function signUp(email: string, password: string, name: string): Promise<void> {
  return new Promise((resolve, reject) => {
    getPool().signUp(
      email.trim().toLowerCase(),
      password,
      [new CognitoUserAttribute({ Name: 'email', Value: email.trim().toLowerCase() }),
       new CognitoUserAttribute({ Name: 'name', Value: name.trim() })],
      [],
      (err) => (err ? reject(err) : resolve()),
    );
  });
}

export function confirmSignUp(email: string, code: string): Promise<void> {
  return new Promise((resolve, reject) => {
    userFor(email).confirmRegistration(code.trim(), true, (err) => (err ? reject(err) : resolve()));
  });
}

export function resendCode(email: string): Promise<void> {
  return new Promise((resolve, reject) => {
    userFor(email).resendConfirmationCode((err) => (err ? reject(err) : resolve()));
  });
}

export function signIn(email: string, password: string): Promise<CognitoUserSession> {
  return new Promise((resolve, reject) => {
    userFor(email).authenticateUser(
      new AuthenticationDetails({ Username: email.trim().toLowerCase(), Password: password }),
      { onSuccess: resolve, onFailure: reject },
    );
  });
}

export function signOut(): void {
  if (!isAuthConfigured()) return;
  getPool().getCurrentUser()?.signOut();
}

/** Current valid session, refreshing transparently; null when signed out. */
export function getSession(): Promise<CognitoUserSession | null> {
  return new Promise((resolve) => {
    if (!isAuthConfigured()) return resolve(null);
    const user = getPool().getCurrentUser();
    if (!user) return resolve(null);
    user.getSession((err: Error | null, session: CognitoUserSession | null) =>
      resolve(err || !session?.isValid() ? null : session));
  });
}

export async function getAccessToken(): Promise<string | null> {
  return (await getSession())?.getAccessToken().getJwtToken() ?? null;
}

export function forgotPassword(email: string): Promise<void> {
  return new Promise((resolve, reject) => {
    userFor(email).forgotPassword({ onSuccess: () => resolve(), onFailure: reject });
  });
}

export function confirmForgotPassword(email: string, code: string, newPassword: string): Promise<void> {
  return new Promise((resolve, reject) => {
    userFor(email).confirmPassword(code.trim(), newPassword, { onSuccess: () => resolve(), onFailure: reject });
  });
}

/** Maps Cognito error codes to messages a person can act on. */
export function describeAuthError(error: unknown): string {
  const code = (error as { code?: string; name?: string })?.code ?? (error as { name?: string })?.name;
  switch (code) {
    case 'NotAuthorizedException': return 'Incorrect email or password.';
    case 'UserNotFoundException': return 'Incorrect email or password.';
    case 'UserNotConfirmedException': return 'Please verify your email first.';
    case 'UsernameExistsException': return 'An account with this email already exists.';
    case 'InvalidPasswordException': return 'Password must be at least 10 characters with upper case, lower case and a number.';
    case 'CodeMismatchException': return 'That verification code is not correct.';
    case 'ExpiredCodeException': return 'That code has expired. Request a new one.';
    case 'LimitExceededException':
    case 'TooManyRequestsException': return 'Too many attempts. Please wait a moment and try again.';
    default: return (error as Error)?.message || 'Something went wrong. Please try again.';
  }
}
